import * as React from "react";
import { useState, useEffect } from "react";
import { LocalizedLink } from "@/components/LocalizedLink";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { FeatureCardSimple } from "@/components/ui/feature-card-simple";
import { Badge } from "@/components/ui/badge";
import { ArrowUp, Circle, Thermometer, Droplets, Wind, Shield, Sparkles, Award, Zap, Users, ArrowRight } from "lucide-react";
import { ProminentCTA } from "@/components/ui/prominent-cta";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { ProductGallery } from "@/components/ui/product-gallery";
import { SEO } from "@/components/SEO";
import { ProductMetadata } from "@/components/ProductMetadata";
import { getProductByName } from "@/data/productReference";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useOptimizedObserver } from "@/hooks/useOptimizedObserver";
import PDFDownloadButton from "@/components/ui/pdf-download-button";
import { RelatedProducts } from "@/components/RelatedProducts";
import { Footer } from "@/components/Footer";
import { ContactForm } from "@/components/ui/contact-form";
import SocialShare from "@/components/blog/SocialShare";
import { ShippingTimeInfo } from "@/components/ShippingTimeInfo";
import { InstallationGuidePDF } from "@/components/InstallationGuidePDF";

// S1 Super Egg Product Images
import s1HeroEgg from "@/assets/s1-hero-egg.jpg";
import s1FeaturesGrid from "@/assets/s1-features-grid.jpg";
import s1EggShapeAward from "@/assets/s1-egg-shape-award.jpg";
import s1WaterTank from "@/assets/s1-water-tank.jpg";
import s1BoosterPump from "@/assets/s1-booster-pump.jpg";
import s1AutoFunctions from "@/assets/s1-auto-functions.jpg";
import s1UsageModes from "@/assets/s1-usage-modes.jpg";
import s1SeatHeating from "@/assets/s1-seat-heating.jpg";

import { PriceFrom } from "@/components/products/PriceFrom";
export default function S1() {
  const [showBackToTop, setShowBackToTop] = useState(false);
  useScrollToTop();
  useOptimizedObserver();

  const productData = getProductByName("S1");

  useEffect(() => {
    let rafId: number | null = null;
    
    const handleScroll = () => {
      if (rafId) return;
      
      rafId = requestAnimationFrame(() => {
        setShowBackToTop(window.scrollY > 400);
        rafId = null;
      });
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const productImages = [
    { src: s1HeroEgg, alt: "S1 Super Egg smart toilet with iconic spherical design in modern bathroom", title: "Iconic Egg Design" },
    { src: s1EggShapeAward, alt: "S1 Super Egg award-winning design with 2022 MUSE Platinum Award", title: "Award Winner" },
    { src: s1AutoFunctions, alt: "S1 Super Egg auto open close and auto flushing demonstration", title: "Auto Functions" },
    { src: s1SeatHeating, alt: "S1 Super Egg 4-level seat heating temperature control system", title: "Seat Heating" },
  ];

  const premiumFeatures = [
    {
      icon: <Circle className="w-8 h-8 text-teal-500" />,
      title: "Patent Egg-Shaped Design",
      description: "Iconic spherical design that's simple, rounded, and perfect for modern bathrooms - 2022 MUSE Platinum Winner"
    },
    {
      icon: <Zap className="w-8 h-8 text-cyan-400" />,
      title: "No Water Pressure Limit",
      description: "Built-in water tank eliminates water pressure limitations - works perfectly in any installation"
    },
    {
      icon: <Droplets className="w-8 h-8 text-blue-400" />,
      title: "Powerful Booster Pump",
      description: "Super spin siphonic flush with strong hydraulic power, anti-odor, water-saving, and low noise operation"
    },
    {
      icon: <Wind className="w-8 h-8 text-cyan-400" />,
      title: "Auto Open/Close & Flush",
      description: "Hands-free operation keeps you clean and hygienic - smart, comfortable, and fully automated"
    },
    {
      icon: <Thermometer className="w-8 h-8 text-orange-400" />,
      title: "4-Level Seat Heating",
      description: "Adjustable seat temperature from normal to 40°C - perfect comfort in summer or winter"
    },
    {
      icon: <Users className="w-8 h-8 text-purple-400" />,
      title: "Various Wash Modes",
      description: "Female wash, rear wash, massage wash, and child wash modes for complete family comfort"
    }
  ];

  const keyFeatures = [
    "Patent egg-shaped spherical design",
    "2022 MUSE Platinum Award Winner",
    "No water pressure limit required",
    "Built-in intelligent water tank",
    "Powerful booster pump system",
    "Auto open/close lid function",
    "Auto flush after use",
    "Foot kick to open seat ring",
    "4-level seat heating (34°C-40°C)",
    "LED large display screen",
    "One-key rotary knob control",
    "Various wash modes for family",
    "Strong hydraulic power flush",
    "Anti-odor and water saving",
    "Low noise operation"
  ];

  const specifications = [
    { label: "Design", value: "Egg-Shaped Spherical" },
    { label: "Award", value: "2022 MUSE Platinum Winner" },
    { label: "Dimensions", value: "580mm × 430mm × 430mm" },
    { label: "Product Weight", value: "58kg" },
    { label: "Water Tank", value: "Built-in intelligent, no pressure limit" },
    { label: "Flush System", value: "Super spin siphonic with booster pump" },
    { label: "Flush Volume", value: "4L / 6L dual flush" },
    { label: "Seat Heating", value: "4 levels (Normal, 34°C, 37°C, 40°C)" },
    { label: "Water Heating", value: "4 levels (Normal, 34°C, 37°C, 40°C)" },
    { label: "Air Dryer", value: "4 levels (Normal, 45°C, 50°C, 55°C)" },
    { label: "Auto Functions", value: "Open/Close, Flush, Sensing" },
    { label: "Wash Modes", value: "Female, Rear, Massage, Child" },
    { label: "Nozzle", value: "ABS antibacterial, self-cleaning" },
    { label: "Bowl Coating", value: "Nano antibacterial glazed" },
    { label: "Control", value: "One-key rotary knob + LED display" },
    { label: "Drain Type", value: "S-trap floor drain" },
    { label: "Power Consumption", value: "1400W (max)" },
    { label: "Voltage", value: "220V / 50Hz" },
    { label: "Waterproof Rating", value: "IPX4" },
    { label: "Safety Features", value: "7-layer protection system" },
    { label: "Warranty", value: "3 Years Comprehensive" },
    { label: "Installation", value: "Professional Required" }
  ];

  const safetyFeatures = [
    { icon: "💧", label: "IPX4 Waterproof" },
    { icon: "⚡", label: "Earth Leakage Protection" },
    { icon: "🔌", label: "Voltage Protection" },
    { icon: "🌡️", label: "Pressure Relief Protection" },
    { icon: "📊", label: "Temperature Sensor" },
    { icon: "🎛️", label: "Mechanical Temp Controller" },
    { icon: "✅", label: "Temp Protection Wire" }
  ];

  const productStructuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "S1 Super Egg Smart Toilet",
    "description": "Award-winning egg-shaped smart toilet featuring patent spherical design, no water pressure limit, powerful booster pump, auto functions, and 4-level seat heating. 2022 MUSE Platinum Award Winner.",
    "brand": {
      "@type": "Brand",
      "name": "Aureum VII Trading"
    },
    "image": [
      "https://www.aureum7trading.com/images/products/s1-hero-egg.jpg"
    ],
    "gtin13": "0795479810016",
    "manufacturer": {
      "@type": "Organization",
      "name": "Aureum VII Trading"
    },
    "sku": "S1-SUPEREGG",
    "mpn": "S1",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "EUR",
      "price": 600,
      "priceValidUntil": "2026-12-31",
      "url": "https://www.aureum7trading.com/products/s1",
      "itemCondition": "https://schema.org/NewCondition",
      "seller": {
        "@type": "Organization",
        "name": "Aureum VII Trading",
        "url": "https://www.aureum7trading.com"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": "0",
          "currency": "EUR"
        },
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"]
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": 1,
            "maxValue": 3,
            "unitCode": "DAY"
          },
          "transitTime": {
            "@type": "QuantitativeValue",
            "minValue": 10,
            "maxValue": 40,
            "unitCode": "DAY"
          }
        }
      },
      "hasMerchantReturnPolicy": {
        "@type": "MerchantReturnPolicy",
        "@id": "https://www.aureum7trading.com/return-policy",
        "applicableCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"],
        "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
        "merchantReturnDays": 14,
        "returnMethod": "https://schema.org/ReturnByMail",
        "returnFees": "https://schema.org/FreeReturn",
        "itemCondition": "https://schema.org/NewCondition",
        "refundType": "https://schema.org/FullRefund"
      }
    },
    "award": "2022 MUSE Platinum Winner"
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-teal-50/30 text-foreground">
      <SEO
        title="S1 Super Egg Smart Toilet | Award-Winning Egg Design"
        description="S1 Super Egg smart toilet: award-winning egg form, booster pump for low-pressure supply, auto functions and 4-level seat heating. CE certified."
        keywords="S1 smart toilet, egg shaped toilet, super egg toilet, spherical toilet design, award winning toilet, MUSE Platinum, auto flush toilet, European smart toilet, luxury bathroom 2025"
        url="/products/s1"
        image="https://www.aureum7trading.com/images/products/s1-hero-egg.jpg"
        structuredData={productStructuredData}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Smart Toilets", url: "/smart-toilets" },
          { name: "S1 Super Egg", url: "/products/s1" }
        ]}
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-20 pb-0 overflow-hidden">
        <div className="relative w-full h-[70vh] md:h-[80vh]">
          <img 
            src={s1HeroEgg} 
            alt="S1 Super Egg smart toilet featuring iconic spherical egg-shaped design in modern minimalist bathroom"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-12">
            <div className="container mx-auto max-w-7xl">
              <Badge variant="outline" className="border-teal-300/50 text-white bg-teal-500/20 backdrop-blur-sm mb-4 font-medium">
                2022 MUSE Platinum Award Winner
              </Badge>
              <h1 className="font-playfair text-5xl md:text-7xl font-light mb-4 text-white tracking-tight">
                S1 Super Egg
                <span className="block text-2xl md:text-3xl text-teal-200 font-medium mt-2">
                  Not Only Progress • Your First Choice
                </span>
              </h1>
              <PriceFrom price={600} tone="light" />
              <p className="font-inter text-xl text-white/90 max-w-2xl mb-6">
                Award-winning egg-shaped design meets cutting-edge technology. Experience the future of bathroom aesthetics.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-white text-primary hover:bg-white/90" asChild>
                  <a href="#features">Explore Features</a>
                </Button>
                <Button variant="outline" size="lg" className="border-white/50 text-white hover:bg-white/10" asChild>
                  <LocalizedLink to="/contact">Request Quote</LocalizedLink>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid Section */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer" id="features">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-playfair text-4xl md:text-5xl font-light mb-6 text-primary">
              Iconic Features<br />Premium Experience
            </h2>
            <p className="font-inter text-xl text-muted-foreground mb-8">
              The S1 Super Egg combines patent egg-shaped design with advanced technology. Features include LED display, one-key rotary knob, no water pressure limit, and various washing modes for complete family comfort.
            </p>
            <div className="grid grid-cols-2 gap-6">
              <div className="text-center p-4 bg-white rounded-xl shadow-soft">
                <Circle className="w-8 h-8 text-teal-500 mx-auto mb-2" />
                <p className="font-medium text-sm">Patent Egg Shape</p>
                <p className="text-xs text-muted-foreground">Award-winning design</p>
              </div>
              <div className="text-center p-4 bg-white rounded-xl shadow-soft">
                <Zap className="w-8 h-8 text-cyan-500 mx-auto mb-2" />
                <p className="font-medium text-sm">No Pressure Limit</p>
                <p className="text-xs text-muted-foreground">Built-in water tank</p>
              </div>
            </div>
          </div>
          <div>
            <img 
              src={s1FeaturesGrid} 
              alt="S1 Super Egg features including patent egg-shaped design, no water pressure limit, LED large screen, one-key rotary knob, and various washing modes"
              className="w-full h-auto rounded-2xl shadow-luxury"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Egg Shape Award Section */}
      <section className="py-16 px-6 lg:px-12 bg-white/70 backdrop-blur-sm">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src={s1EggShapeAward} 
                alt="S1 Super Egg award-winning egg shape design with 2022 MUSE Platinum Award certificate showing simple rounded form perfect for modern bathrooms"
                className="w-full h-auto rounded-2xl shadow-luxury"
                loading="lazy"
              />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Award className="w-10 h-10 text-teal-500" />
                <Badge className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white">2022 MUSE Platinum</Badge>
              </div>
              <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
                Egg Shape<br />Award-Winning Design
              </h2>
              <p className="font-inter text-lg text-muted-foreground mb-6">
                Simple and rounded shape is more suitable for the needs of modern bathrooms. The S1 Super Egg won the prestigious 2022 MUSE Platinum Award for its innovative spherical design.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                  <span className="text-muted-foreground">Patent egg-shaped spherical design</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                  <span className="text-muted-foreground">2022 MUSE Platinum Award Winner</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                  <span className="text-muted-foreground">Perfect for modern bathroom aesthetics</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* No Water Pressure Limit */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
              No Water Pressure<br />Limit Intelligent
            </h2>
            <p className="font-inter text-lg text-muted-foreground mb-6">
              Built-in water tank means no water pressure limit. You don't need to worry about water pressure limitations - the S1 works perfectly in any installation scenario.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                <span className="text-muted-foreground">Built-in intelligent water tank</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                <span className="text-muted-foreground">No external water pressure requirements</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                <span className="text-muted-foreground">Perfect for any installation</span>
              </li>
            </ul>
          </div>
          <div>
            <img 
              src={s1WaterTank} 
              alt="S1 Super Egg built-in water tank system showing no water pressure limit intelligent technology"
              className="w-full h-auto rounded-2xl shadow-luxury"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Powerful Booster Pump */}
      <section className="py-16 px-6 lg:px-12 bg-stone-100/50">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src={s1BoosterPump} 
                alt="S1 Super Egg powerful booster pump with super spin siphonic system showing strong hydraulic power, anti-odor, water saving, and low noise features"
                className="w-full h-auto rounded-2xl shadow-luxury"
                loading="lazy"
              />
            </div>
            <div>
              <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
                Powerful Booster Pump
              </h2>
              <p className="font-inter text-lg text-muted-foreground mb-6">
                Super spin siphonic flush with strong water flow cleans with one flush. Advanced engineering delivers strong hydraulic power while maintaining anti-odor, water-saving, and low noise operation.
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-xl text-center">
                  <Droplets className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                  <p className="text-xs font-medium">Strong Power</p>
                </div>
                <div className="bg-white p-4 rounded-xl text-center">
                  <Shield className="w-6 h-6 text-green-500 mx-auto mb-2" />
                  <p className="text-xs font-medium">Anti-Odor</p>
                </div>
                <div className="bg-white p-4 rounded-xl text-center">
                  <Wind className="w-6 h-6 text-cyan-500 mx-auto mb-2" />
                  <p className="text-xs font-medium">Low Noise</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Auto Functions */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
              Auto Open / Close<br />Auto Flushing
            </h2>
            <p className="font-inter text-lg text-muted-foreground mb-6">
              Keep hands-free at all times. Stay clean and hygienic with smart and comfortable automated functions for both men and women.
            </p>
            <div className="bg-white p-6 rounded-xl shadow-soft">
              <h3 className="font-medium mb-4">Smart Usage Sequence</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium text-primary mb-2">Men Use</p>
                  <ul className="space-y-1 text-muted-foreground text-xs">
                    <li>• Auto open lid when close (20cm)</li>
                    <li>• Foot kick to open seat ring</li>
                    <li>• Stand position using</li>
                    <li>• Auto flush and close lid</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-primary mb-2">Women Use</p>
                  <ul className="space-y-1 text-muted-foreground text-xs">
                    <li>• Auto open lid when close (20cm)</li>
                    <li>• Sit position using</li>
                    <li>• Auto flush after leaving</li>
                    <li>• Auto close lid after flushing</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div>
            <img 
              src={s1AutoFunctions} 
              alt="S1 Super Egg auto open close and auto flushing functions demonstrating hands-free hygienic operation"
              className="w-full h-auto rounded-2xl shadow-luxury"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Seat Heating */}
      <section className="py-16 px-6 lg:px-12 bg-white/70 backdrop-blur-sm">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src={s1SeatHeating} 
                alt="S1 Super Egg 4-level seat heating system with temperature adjustment from normal to 40°C showing LED display"
                className="w-full h-auto rounded-2xl shadow-luxury"
                loading="lazy"
              />
            </div>
            <div>
              <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
                Seat Ring Heating<br />4-Levels Adjust
              </h2>
              <p className="font-inter text-lg text-muted-foreground mb-6">
                No matter whether in summer or winter, you can find the suitable temperature. Four levels of seat temperature ensure perfect comfort year-round.
              </p>
              <div className="flex items-center gap-2 mb-4">
                <div className="flex-1 h-2 bg-gradient-to-r from-blue-400 via-yellow-400 to-red-400 rounded-full"></div>
              </div>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground">Normal</p>
                  <p className="font-medium text-sm">Off</p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground">Level 1</p>
                  <p className="font-medium text-sm">34°C</p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground">Level 2</p>
                  <p className="font-medium text-sm">37°C</p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground">Level 3</p>
                  <p className="font-medium text-sm">40°C</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Reference Information */}
      {productData && (
        <section className="py-8 px-6 lg:px-12 bg-muted/20 container mx-auto max-w-4xl fade-in-observer">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-playfair text-xl font-medium text-primary">Product Information</h2>
            <Badge variant="outline" className="text-xs">Reference System Active</Badge>
          </div>
          <ProductMetadata product={productData} showInternalCodes={true} className="mb-4" />
        </section>
      )}

      {/* Product Gallery */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer">
        <h2 className="font-playfair text-3xl font-medium text-primary mb-12 text-center">Design Gallery</h2>
        <ProductGallery images={productImages} className="max-w-4xl mx-auto" />
      </section>

      {/* Premium Features Grid */}
      <section className="py-16 px-6 lg:px-12 bg-white/70 backdrop-blur-sm container mx-auto max-w-7xl fade-in-observer">
        <h2 className="font-playfair text-3xl font-medium text-primary mb-4 text-center">Premium Smart Features</h2>
        <p className="font-inter text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
          Experience advanced technology combined with award-winning egg-shaped design aesthetics.
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {premiumFeatures.map((feature, index) => (
            <FeatureCardSimple
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>
      </section>

      {/* Complete Feature List */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer">
        <h2 className="font-playfair text-3xl font-medium text-primary mb-12 text-center">Complete Features</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {keyFeatures.map((feature, index) => (
            <div key={index} className="shadow-soft bg-white rounded-lg p-6 text-center">
              <Circle className="w-8 h-8 text-teal-500 mx-auto mb-3" />
              <p className="font-inter text-muted-foreground">{feature}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-16 px-6 lg:px-12 bg-white/70 backdrop-blur-sm fade-in-observer">
        <div className="container mx-auto max-w-5xl">
          <h2 className="font-playfair text-3xl font-medium text-primary mb-4 text-center">Technical Specifications</h2>
          <p className="font-inter text-muted-foreground text-center mb-12">Complete technical details for the S1 Super Egg</p>
          
          {/* Specifications Table */}
          <div className="bg-white rounded-2xl shadow-luxury overflow-hidden mb-12">
            <div className="grid md:grid-cols-2">
              {specifications.map((spec, index) => (
                <div 
                  key={index} 
                  className={`flex justify-between items-center p-4 ${
                    index % 2 === 0 ? 'bg-white' : 'bg-teal-50/30'
                  } border-b border-teal-100/50`}
                >
                  <span className="text-sm font-medium text-muted-foreground">{spec.label}</span>
                  <span className="text-sm font-semibold text-primary text-right">{spec.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 7 Safety Protection */}
          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-8">
            <h3 className="font-playfair text-2xl font-medium text-primary mb-6 text-center">7-Layer Safety Protection</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
              {safetyFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl p-4 text-center shadow-soft">
                  <span className="text-2xl mb-2 block">{feature.icon}</span>
                  <span className="text-xs font-medium text-muted-foreground">{feature.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Installation Guide */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-5xl fade-in-observer" id="installation">
        <h2 className="font-playfair text-3xl font-medium text-primary mb-4 text-center">Installation Guide</h2>
        <p className="font-inter text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
          Professional installation recommended. Follow these step-by-step instructions for optimal S1 Super Egg setup.
        </p>
        
        {/* Plumbing Requirements */}
        <div className="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-8 mb-8 shadow-soft">
          <h3 className="font-playfair text-xl font-medium text-primary mb-6 flex items-center gap-2">
            <Droplets className="w-6 h-6 text-cyan-500" />
            Plumbing Requirements
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-sm font-bold text-teal-700">1</span>
                </div>
                <div>
                  <p className="font-medium text-primary">Water Supply</p>
                  <p className="text-sm text-muted-foreground">Cold water connection: 1/2" (G1/2) standard fitting. Built-in tank: No water pressure limit required!</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-sm font-bold text-teal-700">2</span>
                </div>
                <div>
                  <p className="font-medium text-primary">Drain Connection</p>
                  <p className="text-sm text-muted-foreground">S-trap floor drain standard. Rough-in distance: 300-400mm. Drain pipe: 100mm diameter</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-sm font-bold text-teal-700">3</span>
                </div>
                <div>
                  <p className="font-medium text-primary">Floor Requirements</p>
                  <p className="text-sm text-muted-foreground">Level, solid floor surface. Minimum clearance: 580mm × 430mm footprint. Product weight: 58kg</p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Zap className="w-4 h-4 text-cyan-700" />
                </div>
                <div>
                  <p className="font-medium text-primary">Electrical Requirements</p>
                  <p className="text-sm text-muted-foreground">220V/50Hz power supply. Max power: 1400W. Dedicated grounded outlet within 1m. Circuit breaker: 16A recommended</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Shield className="w-4 h-4 text-green-700" />
                </div>
                <div>
                  <p className="font-medium text-primary">Safety Clearance</p>
                  <p className="text-sm text-muted-foreground">Minimum 150mm side clearance. Front clearance: 500mm recommended. Sensor range: 20cm for auto functions</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step-by-Step Installation */}
        <div className="bg-white rounded-2xl p-8 shadow-luxury">
          <h3 className="font-playfair text-xl font-medium text-primary mb-6">Step-by-Step Installation</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="border-l-4 border-teal-400 pl-4 py-2">
                <p className="font-medium text-primary">Step 1: Preparation</p>
                <p className="text-sm text-muted-foreground">Turn off water supply. Remove old toilet. Clean floor surface thoroughly. Note: S1 weighs 58kg - two people recommended.</p>
              </div>
              <div className="border-l-4 border-teal-400 pl-4 py-2">
                <p className="font-medium text-primary">Step 2: Position Flange</p>
                <p className="text-sm text-muted-foreground">Install new wax ring or gasket on drain flange. Verify 300-400mm rough-in distance matches S1 outlet.</p>
              </div>
              <div className="border-l-4 border-teal-400 pl-4 py-2">
                <p className="font-medium text-primary">Step 3: Set Toilet</p>
                <p className="text-sm text-muted-foreground">Carefully lower S1 onto flange with two people. Align egg shape with wall. Press firmly to seal. Level using shims.</p>
              </div>
              <div className="border-l-4 border-teal-400 pl-4 py-2">
                <p className="font-medium text-primary">Step 4: Secure Base</p>
                <p className="text-sm text-muted-foreground">Install and tighten floor bolts evenly. Apply silicone sealant around base for watertight seal. Allow 24h cure time.</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="border-l-4 border-cyan-400 pl-4 py-2">
                <p className="font-medium text-primary">Step 5: Water Connection</p>
                <p className="text-sm text-muted-foreground">Connect braided supply line to inlet valve. S1's built-in tank handles any pressure. Check connections for leaks.</p>
              </div>
              <div className="border-l-4 border-cyan-400 pl-4 py-2">
                <p className="font-medium text-primary">Step 6: Electrical Setup</p>
                <p className="text-sm text-muted-foreground">Plug power cord into grounded GFCI outlet. Verify IPX4 protection. Test LED display powers on correctly.</p>
              </div>
              <div className="border-l-4 border-cyan-400 pl-4 py-2">
                <p className="font-medium text-primary">Step 7: System Test</p>
                <p className="text-sm text-muted-foreground">Turn on water. Test dual flush (4L/6L). Verify booster pump operation. Test auto open/close and sensing functions.</p>
              </div>
              <div className="border-l-4 border-green-400 pl-4 py-2">
                <p className="font-medium text-primary">Step 8: Final Check</p>
                <p className="text-sm text-muted-foreground">Test all wash modes (female, rear, massage, child). Verify 4-level seat heating. Configure preferences via rotary knob.</p>
              </div>
            </div>
          </div>
          
          {/* Important Notes */}
          <div className="mt-8 bg-teal-50 border border-teal-200 rounded-xl p-6">
            <h4 className="font-medium text-primary mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5 text-teal-600" />
              Important Installation Notes
            </h4>
            <ul className="grid md:grid-cols-2 gap-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-teal-500">•</span>
                Professional installation strongly recommended
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-500">•</span>
                Two-person lift required (58kg weight)
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-500">•</span>
                7-layer safety protection pre-installed
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-500">•</span>
                Keep 20cm clear for auto-sensing to function
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-500">•</span>
                3-year warranty requires certified installation
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-500">•</span>
                Contact us for professional installer referrals
              </li>
            </ul>
          </div>
          
          {/* PDF Download */}
          <div className="mt-6 flex justify-center">
            <InstallationGuidePDF product="S1" />
          </div>
        </div>
      </section>

      {/* Shipping Info */}
      <section className="py-12 px-6 lg:px-12 container mx-auto max-w-4xl fade-in-observer">
        <ShippingTimeInfo />
      </section>

      {/* Comparison CTA */}
      <section className="py-12 px-6 lg:px-12 bg-gradient-to-r from-teal-50 to-amber-50 fade-in-observer">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-playfair text-2xl md:text-3xl font-medium text-primary mb-4">
            Can't Decide Between Egg and Square Design?
          </h2>
          <p className="font-inter text-muted-foreground mb-6 max-w-2xl mx-auto">
            Compare the S1 Super Egg's award-winning spherical design with the K2 Quadrate's revolutionary square aesthetics.
          </p>
          <Button size="lg" asChild className="bg-gradient-to-r from-teal-500 to-amber-500 hover:from-teal-600 hover:to-amber-600">
            <LocalizedLink to="/compare/k2-vs-s1">
              Compare S1 vs K2
              <ArrowRight className="w-5 h-5 ml-2" />
            </LocalizedLink>
          </Button>
        </div>
      </section>

      {/* CTA Section */}
      <ProminentCTA 
        title="Experience the S1 Super Egg"
        description="Ready to transform your bathroom with award-winning egg-shaped design? Contact our team for pricing and installation."
        primaryCTA={{ text: "Request Quote", href: "/contact" }}
        secondaryCTA={{ text: "View All Models", href: "/smart-toilets" }}
        showContactButtons={true}
      />

      {/* Related Products */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer">
        <RelatedProducts currentProduct="S1" />
      </section>

      {/* Contact Form */}
      <section className="py-16 px-6 lg:px-12 bg-muted/30 fade-in-observer">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-playfair text-3xl font-medium text-primary mb-4 text-center">Get S1 Super Egg Information</h2>
          <p className="font-inter text-muted-foreground text-center mb-8">
            Request pricing, specifications, or schedule a consultation with our team.
          </p>
          <ContactForm />
        </div>
      </section>

      {/* Social Share */}
      <section className="py-8 px-6 container mx-auto max-w-4xl">
        <SocialShare 
          url="https://www.aureum7trading.com/products/s1" 
          title="S1 Super Egg Smart Toilet - Award-Winning Egg Design" 
        />
      </section>

      <Footer />

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 bg-primary hover:bg-primary/90 text-primary-foreground p-3 rounded-full shadow-lg transition-all duration-300"
          aria-label="Back to top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
